import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";
import { DataTable } from "@/components/data-table/DataTable";
import { ColumnToggleMenu, SavedViewsMenu, FilterChips } from "@/components/data-table/TableControls";
import { useTablePrefs } from "@/components/data-table/useTablePrefs";
import { useToast } from "@/components/SimpleToast";

/**
 * Einheitliche, serverseitige Listen-Ansicht: Toolbar (Suche + Filter + Spalten +
 * Ansichten), Filter-Chips, Sammelaktionen, Tabelle und Pagination – alle
 * Präferenzen pro Nutzer gespeichert. Wird von mehreren Seiten wiederverwendet.
 */
export function TableList({
  tableKey,
  columns,
  fetcher,                 // (params) => Promise<{items,total}>
  requiredColumns = [],
  defaultColumns,
  defaultSort = { by: "created_at", dir: "desc" },
  defaultFilters = {},
  filterControls = [],     // [{key, options:[{value,label}], defaultLabel, width}]
  chipLabel,               // (key, value) => string | null
  searchPlaceholder = "Suchen…",
  rowActions,
  bulkActions = [],        // [{label, icon, danger, onClick:(ids)=>Promise}]
  onRowClick,
  emptyState,
  mobileCard,
  refreshSignal = 0,
  testid = "table-list",
}) {
  const toast = useToast();
  const defaultPrefs = {
    columns: defaultColumns || columns.map((c) => c.key),
    sort_by: defaultSort.by, sort_dir: defaultSort.dir, page_size: 25,
    filters: { search: "", ...defaultFilters },
  };
  const { prefs, setPrefs, views, saveView, deleteView } = useTablePrefs(tableKey, defaultPrefs);
  const f = prefs.filters || {};

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [searchInput, setSearchInput] = useState(f.search || "");
  const [activeViewId, setActiveViewId] = useState(null);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...f, sort_by: prefs.sort_by, sort_dir: prefs.sort_dir, page, page_size: prefs.page_size };
      Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] === "all" || params[k] === false) delete params[k]; });
      const { items, total: t } = await fetcher(params);
      setRows(Array.isArray(items) ? items : []);
      setTotal(t || 0);
    } catch (e) { console.debug("Tabelle laden:", e); setError("Die Liste konnte nicht geladen werden. Bitte prüfen Sie Ihre Verbindung."); setRows([]); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(f), prefs.sort_by, prefs.sort_dir, prefs.page_size, page, refreshSignal]);

  useEffect(() => { load(); }, [load]);

  // debounce search
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    const t = setTimeout(() => { if ((f.search || "") !== searchInput) setFilters({ search: searchInput }); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setFilters = (patch) => { setPrefs((p) => ({ ...p, filters: { ...p.filters, ...patch } })); setPage(1); setActiveViewId(null); };
  const onSort = (key) => setPrefs((p) => ({ ...p, sort_by: key, sort_dir: p.sort_by === key && p.sort_dir === "asc" ? "desc" : "asc" }));
  const setColumns = (cols) => setPrefs((p) => ({ ...p, columns: cols }));

  const toggleRow = (id) => setSelection((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelection((prev) => { const ids = rows.map((r) => r.id); const every = ids.every((id) => prev.has(id)); const n = new Set(prev); every ? ids.forEach((id) => n.delete(id)) : ids.forEach((id) => n.add(id)); return n; });
  const clearSelection = () => setSelection(new Set());

  const chips = [];
  Object.keys(f).forEach((k) => {
    if (k === "search") return;
    const val = f[k];
    if (val === "" || val === "all" || val === false || val == null) return;
    const label = chipLabel ? chipLabel(k, val) : `${k}: ${val}`;
    if (label) chips.push({ key: k, label });
  });
  const removeChip = (key) => { const dv = defaultFilters[key]; setFilters({ [key]: dv !== undefined ? dv : "all" }); };
  const clearAll = () => { setSearchInput(""); setPrefs((p) => ({ ...p, filters: { search: "", ...defaultFilters } })); setPage(1); setActiveViewId(null); };

  const applyView = (v) => {
    const c = v.config || {};
    const next = { ...prefs, ...c, filters: { search: "", ...defaultFilters, ...(c.filters || {}) } };
    setPrefs(() => next); setSearchInput(next.filters.search || ""); setPage(1); setActiveViewId(v.id);
  };
  const onSaveView = async (name) => {
    try { const v = await saveView(name, { filters: prefs.filters, columns: prefs.columns, sort_by: prefs.sort_by, sort_dir: prefs.sort_dir, page_size: prefs.page_size }); setActiveViewId(v.id); toast.success(`Ansicht „${name}" gespeichert`); }
    catch { toast.error("Ansicht konnte nicht gespeichert werden"); }
  };
  const onDeleteView = async (id) => { try { await deleteView(id); if (activeViewId === id) setActiveViewId(null); } catch { toast.error("Löschen fehlgeschlagen"); } };

  const runBulk = async (action) => {
    const ids = Array.from(selection);
    try { await action.onClick(ids); clearSelection(); load(); }
    catch { toast.error("Massenaktion fehlgeschlagen"); }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={searchPlaceholder} className="pl-9" data-testid={`${testid}-search`} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterControls.map((fc) => (
            <Select key={fc.key} value={f[fc.key] ?? "all"} onValueChange={(v) => setFilters({ [fc.key]: v })}>
              <SelectTrigger className="h-9" style={{ width: fc.width || 150 }} data-testid={`filter-${fc.key}`}><SelectValue placeholder={fc.defaultLabel} /></SelectTrigger>
              <SelectContent>
                {fc.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <ColumnToggleMenu columns={columns} visibleColumns={prefs.columns} onChange={setColumns} requiredKeys={requiredColumns} />
          <SavedViewsMenu views={views} activeViewId={activeViewId} onApply={applyView} onSave={onSaveView} onDelete={onDeleteView} />
        </div>
      </div>
      <FilterChips chips={chips} onRemove={removeChip} onClearAll={clearAll} />

      {selection.size > 0 && bulkActions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selection.size} ausgewählt</span>
          <div className="h-4 w-px bg-border mx-1" />
          {bulkActions.map((a) => (
            <Button key={a.label} variant="outline" size="sm" className={a.danger ? "text-destructive hover:text-destructive" : ""} onClick={() => runBulk(a)} data-testid={`bulk-${a.label.toLowerCase()}`}>
              {a.icon && <a.icon className="w-4 h-4 mr-1.5" />}{a.label}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="bulk-clear-btn"><X className="w-4 h-4 mr-1.5" />Auswahl aufheben</Button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        sort={{ by: prefs.sort_by, dir: prefs.sort_dir }}
        onSort={onSort}
        visibleColumns={prefs.columns}
        selection={selection}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        onRowClick={onRowClick}
        rowActions={rowActions}
        page={page}
        pageSize={prefs.page_size}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(n) => setPrefs((p) => ({ ...p, page_size: n }))}
        testid={`${testid}-table`}
        emptyState={emptyState}
        mobileCard={mobileCard}
        error={error}
        onRetry={load}
      />
    </div>
  );
}

export default TableList;
